import {Typography} from '@mui/material';
import React, {useRef, useState} from 'react';
import PhotoModal from './PhotoModal';
import {GalleryImage, photoUrl} from './GalleryImage';

// Slideshow cu navigare manuala in bucla (pasul 20): tap pe treimea stanga/dreapta a
// imaginii, swipe orizontal, sagetile tastaturii; tap in centru = marire (PhotoModal).
// `faraMarire` (29.4): fara marire - ex. lucrare la nivelul „previzualizare” (fotografie estompata).
const SWIPE_MIN = 50;

export default function PhotoGallery({photos = [], title, faraMarire = false}) {
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(0);
    const swipe = useRef(null);
    const swiped = useRef(false);
    const items = photos.filter((photo) => photo.poza);
    const index = Math.min(selected, Math.max(0, items.length - 1));
    const photo = items[index];

    if (!photo) return <Typography color="text.secondary" className="gallery-empty">Fotografiile vor fi adăugate în curând.</Typography>;

    const label = photo.descriere || `${title} — fotografia ${index + 1}`;
    const multe = items.length > 1;
    const go = (delta) => setSelected((index + delta + items.length) % items.length);
    // Dupa un swipe, click-ul generat de browser nu trebuie sa mai navigheze/mareasca.
    const guard = (action) => () => { if (swiped.current) { swiped.current = false; return; } action(); };
    const onPointerDown = (event) => { swipe.current = {x: event.clientX, y: event.clientY}; swiped.current = false; };
    const onPointerUp = (event) => {
        const start = swipe.current;
        swipe.current = null;
        if (!start || !multe) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy) * 1.2) {
            swiped.current = true;
            go(dx < 0 ? 1 : -1);
        }
    };
    const onKeyDown = (event) => {
        if (!multe) return;
        if (event.key === 'ArrowRight') { event.preventDefault(); go(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); go(-1); }
    };
    return (
        <section className="photo-gallery" aria-label={`Fotografii: ${title}`}>
            <figure className="gallery-feature" aria-roledescription={multe ? 'carusel' : undefined}>
                <div className="gallery-stage" onPointerDown={onPointerDown} onPointerUp={onPointerUp}
                     onPointerCancel={() => { swipe.current = null; }} onKeyDown={onKeyDown}>
                    {faraMarire
                        ? <div className="gallery-enlarge gallery-enlarge--fara-marire">
                            <GalleryImage key={photo.poza} src={photoUrl(photo.poza)} alt={label} draggable={false} />
                        </div>
                        : <button type="button" className="gallery-enlarge" onClick={guard(() => setOpen(true))}
                                  aria-label={`Mărește fotografia: ${label}`}>
                            <GalleryImage key={photo.poza} src={photoUrl(photo.poza)} alt={label} draggable={false} />
                        </button>}
                    {multe && <>
                        <button type="button" className="gallery-tap gallery-tap--prev" aria-label="Fotografia anterioară"
                                onClick={guard(() => go(-1))}><span className="gallery-arrow" aria-hidden="true">‹</span></button>
                        <button type="button" className="gallery-tap gallery-tap--next" aria-label="Fotografia următoare"
                                onClick={guard(() => go(1))}><span className="gallery-arrow" aria-hidden="true">›</span></button>
                    </>}
                </div>
                <figcaption>
                    <span>{label}</span>
                    <Typography component="span" color="text.secondary" aria-live="polite">{index + 1} / {items.length}</Typography>
                </figcaption>
            </figure>
            {multe && (
                <div className="gallery-thumbnails" aria-label="Alege fotografia">
                    {items.map((item, position) => (
                        <button key={item.id ?? item.poza} type="button"
                                className={`gallery-thumbnail${index === position ? ' selected' : ''}`}
                                aria-pressed={index === position}
                                aria-label={`Fotografia ${position + 1}: ${item.descriere || title}`}
                                onClick={() => setSelected(position)}>
                            <GalleryImage key={item.poza} src={photoUrl(item.poza)} alt="" loading="lazy" />
                        </button>
                    ))}
                </div>
            )}
            {open && !faraMarire && <PhotoModal photos={items} index={index} title={title} onSelect={setSelected} onClose={() => setOpen(false)} />}
        </section>
    );
}
